

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { eventCoreSchema } from '@/lib/schemas/event';


export function BasicIdentitySection() {
  const {
    register,
    formState: { errors },
  } = useFormContext<z.infer<typeof eventCoreSchema>>();
  const resolveResponsive = useResponsiveValue();

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 0.5 }}>
        Event Identity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Define the core details of the event.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('name')}
            label="Event Name"
            placeholder="e.g. The Battle of Geonosis"
            fullWidth
            variant="standard"
            error={!!errors.name}
            helperText={errors.name?.message}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInput-root': {
                height: 40,
                fontSize: '0.9rem',
                '&:before': { borderBottomWidth: 1 },
                '&:after': { borderBottomWidth: 2 }
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
           <TextField
            {...register('type')}
            label="Event Type"
            placeholder="e.g. Battle, Treaty, Discovery"
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.type}
            helperText={errors.type?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
                '& .MuiFilledInput-root': { height: 44 }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('date')}
            label="Date / Timestamp"
            placeholder="e.g. 1999 A.D. or Year 5 Era 2"
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.date}
            helperText={errors.date?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': { height: 44 }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('shortDescription')}
            label="Summary"
            placeholder="Briefly describe what happens."
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            size="small"
            error={!!errors.shortDescription}
            helperText={errors.shortDescription?.message}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

         <Grid size={resolveResponsive({ xs: 12 })}>
            <TextField
            {...register('whyItMatters')}
            label="Significance"
            placeholder="Why is this event important to the story?"
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            size="small"
            error={!!errors.whyItMatters}
            helperText={errors.whyItMatters?.message}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
            />
         </Grid>
      </Grid>

    </Paper>
  );
}
